import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SchedulerItemOnMachineCRUD } from '../dto/scheduler-item-on-machine.dto';
import { User } from '../types/users';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Suffix } from '../types/suffix';
import { IApiBackendRouteService } from '../abstract/api-routes.service';
import { WorkOrder } from '../types/work-order';
import { API_ENDPOINTS } from '../../../core/providers/constants/api.constants';
import { WorkOrderGroup } from '../types/work-order-group';
import { Stage } from '../types/stages';
import { SchedulingApiRoutesDto } from '../dto/scheduling.dto';
import { Machine } from '../types/machine';
import {
  CreateReportOrderDto,
  ReportWorkorderInterface,
} from '../types/workorder-report-dto';
import { SchedulerWorkOrder } from '../../../common/scheduler/models/work-order';
import { SchedulerMachineWorkOrder } from '../../../common/scheduler/models/wo-machine';
import { SchedulerSubWorkOrder } from '../../../common/scheduler/models/sub-work-order';
import { SchedulerMachineSubWorkOrder } from '../../../common/scheduler/models/sub-machine';
import { SchedulerSubWorkOrderOnMachine } from '../../../common/scheduler/models/sub-wo-on-machine';
import { SchedulerWorkOrderOnMachine } from '../../../common/scheduler/models/wo-on-machine';
import { SubWorkOrder } from '../types/sub-work-order';
import { MachineWorkOrder } from '../types/machine-work-order';

type AddBody = SchedulerItemOnMachineCRUD['add'];
type UpdateBody = SchedulerItemOnMachineCRUD['update'];
type StopBody = SchedulerItemOnMachineCRUD['stop'];
type ItemOperations = SchedulerItemOnMachineCRUD['getOperations'];

@Injectable({
  providedIn: 'root',
})
export class ApiRouteSchedulingService extends IApiBackendRouteService {
  constructor(protected readonly http: HttpClient) {
    super();
  }

  urlRoute = `${API_ENDPOINTS.SCHEDULER_PROCESS}`;

  getWorkOrderSchedulingData(
    typeId: number,
    query: string = ''
  ): Observable<{
    workOrders: SchedulerWorkOrder[];
    machines: SchedulerMachineWorkOrder[];
    operators: User[];
  }> {
    return this.http.get<{
      workOrders: SchedulerWorkOrder[];
      machines: SchedulerMachineWorkOrder[];
      operators: User[];
    }>(`${this.urlRoute}/${typeId}${query ? '?' + query : ''}`);
  }

  getSubWorkOrderSchedulingData(query: string = ''): Observable<{
    subWorkOrders: SchedulerSubWorkOrder[];
    machines: SchedulerMachineSubWorkOrder[];
    operators: User[];
  }> {
    return this.http.get<{
      subWorkOrders: SchedulerSubWorkOrder[];
      machines: SchedulerMachineSubWorkOrder[];
      operators: User[];
    }>(`${this.urlRoute}/data/subWorkOrders/${query ? '?' + query : ''}`);
  }

  getOperatorSchedulingData(
    employeeId: number,
    query: string = ''
  ): Observable<{
    workOrders: SchedulerWorkOrder[];
    machines: SchedulerMachineWorkOrder[];
    operator: User;
  }> {
    return this.http.get<{
      workOrders: SchedulerWorkOrder[];
      machines: SchedulerMachineWorkOrder[];
      operator: User;
    }>(
      `${this.urlRoute}/data-by-operator/${employeeId}${
        query ? '?' + query : ''
      }`
    );
  }

  addSubWorkOrderToMachine(
    body: AddBody
  ): Observable<SchedulerSubWorkOrderOnMachine> {
    return this.http.post<SchedulerSubWorkOrderOnMachine>(
      `${this.urlRoute}/add-to-machine/subWorkOrder`,
      {
        ...body,
        subWorkOrderId: body.itemId,
      }
    );
  }

  moveSubWorkOrderToOtherMachine(
    body: UpdateBody
  ): Observable<SchedulerSubWorkOrderOnMachine> {
    const newBody = {
      ...body,
      subWorkOrderId: body.itemId,
    };
    return this.http.patch<SchedulerSubWorkOrderOnMachine>(
      `${this.urlRoute}/moving-sub-wo`,
      newBody
    );
  }

  removeSubWorkOrderFromMachine(
    subWorkOrderId: number,
    machineId: number
  ): Observable<any> {
    return this.http.patch(`${this.urlRoute}/moving-sub-wo`, {
      subWorkOrderId,
      previousMachineId: machineId,
      toMachine: false,
    });
  }

  startSubWorkOrderOnMachine(
    subWorkOrderId: number,
    machineId: number
  ): Observable<any> {
    return this.http.post(`${this.urlRoute}/start-sub-order`, {
      subWorkOrderId,
      machineId,
      starting: true,
    });
  }

  stopSubWorkOrderOnMachine(body: StopBody): Observable<{
    currentSubWorkOrder: SchedulerSubWorkOrder;
    newSubWorkOrder: SchedulerSubWorkOrder;
  }> {
    const newBody = {
      ...body,
      subWorkOrderId: body.itemId,
      starting: false,
    };
    return this.http.post<{
      currentSubWorkOrder: SchedulerSubWorkOrder;
      newSubWorkOrder: SchedulerSubWorkOrder;
    }>(`${this.urlRoute}/end-sub-order`, newBody);
  }

  addWorkOrderToMachine(
    body: AddBody
  ): Observable<SchedulerWorkOrderOnMachine> {
    return this.http.post<SchedulerWorkOrderOnMachine>(
      `${this.urlRoute}/add-to-machine/workOrder`,
      {
        ...body,
        workOrderId: body.itemId,
        transactionId: body.itemId,
      }
    );
  }

  moveWorkOrderToOtherMachine(
    body: UpdateBody
  ): Observable<SchedulerWorkOrderOnMachine> {
    const newBody = {
      ...body,
      workOrderId: body.itemId,
    };
    return this.http.patch<SchedulerWorkOrderOnMachine>(
      `${this.urlRoute}/moving-wo`,
      newBody
    );
  }

  removeWorkOrderFromMachine(
    workOrderId: number,
    machineId: number
  ): Observable<any> {
    return this.http.patch(`${this.urlRoute}/moving-wo`, {
      workOrderId,
      previousMachineId: machineId,
      toMachine: false,
    });
  }

  startWorkOrderOnMachine(
    workOrderId: number,
    machineId: number
  ): Observable<any> {
    return this.http.post(`${this.urlRoute}/start-order`, {
      workOrderId,
      machineId,
      // starting: true,
    });
  }

  stopWorkOrderOnMachine(body: StopBody): Observable<
    | SchedulerWorkOrder
    | {
        workOrder: SchedulerWorkOrder;
        newSubWorkOrder: SchedulerWorkOrder;
      }
  > {
    const newBody = {
      ...body,
      workOrderId: body.itemId,
      // starting: false,
    };
    return this.http.post<SchedulerWorkOrder>(
      `${this.urlRoute}/end-order`,
      newBody
    );
  }

  setItemRunning(
    itemId: number,
    machineId: number,
    options?: {}
  ): Observable<any> {
    return this.http.post(`${this.urlRoute}/work-order/start`, {
      itemId,
      machineId,
      ...options,
    });
  }

  getStopInfo(data: {
    itemId: number;
    machineId: number;
  }): Observable<ItemOperations> {
    return this.http.post<ItemOperations>(
      `${this.urlRoute}/work-order/stop-info`,
      {
        ...data,
      }
    );
  }

  getSelectableMolds(departmentId: number | string): Observable<Suffix[]> {
    return this.http.get<Suffix[]>(
      `${this.urlRoute}/${departmentId}/selectable-molds`
    );
  }

  setNewMoldForMachine(body: {
    machineId: number;
    suffixId: number;
    previousSuffixId: number;
    departmentId: number;
  }): Observable<Suffix> {
    return this.http.post<Suffix>(`${this.urlRoute}/machine/select-mold`, body);
  }

  deleteWorkOrder(workOrderId: number, active = false): Observable<any> {
    return this.http.delete(`${this.urlRoute}/remove/work-order`, {
      body: {
        onSchedule: active,
        workOrderId,
      },
    });
  }

  getSchedulingWorkOrderData(
    typeId: number,
    query = ''
  ): Observable<WorkOrder[]> {
    return this.http.get<WorkOrder[]>(
      `${this.urlRoute}/work-orders/${typeId}${query ? '?' + query : ''}`
    );
  }

  createRelation(body: {
    parentId: number;
    memberId: number;
    current?: boolean;
    multiple?: boolean;
  }): Observable<WorkOrderGroup> {
    return this.http.post<WorkOrderGroup>(
      `${this.urlRoute}/relation/create`,
      body
    );
  }

  closeRelation(
    relationId: number,
    body: {
      quantity: number;
      typeOfId?: string;
      force?: boolean;
    }
  ): Observable<any> {
    return this.http.patch(
      `${this.urlRoute}/relation/${relationId}/close`,
      body
    );
  }

  getNextStages(currentStageId: number): Observable<Stage[]> {
    return this.http.get<Stage[]>(
      `${this.urlRoute}/${currentStageId}/next-stages`
    );
  }

  // Routes with work order

  registerProgressPlanningWorkOrder(
    body: SchedulingApiRoutesDto['registerProgress']['body']
  ): Observable<any> {
    return this.http.post(
      `${this.urlRoute}/planning-wo/register-progress`,
      body
    );
  }

  registerProgressWorkOrder(
    body: SchedulingApiRoutesDto['registerProgress']['body']
  ): Observable<any> {
    return this.http.post(
      `${this.urlRoute}/work-order/register-progress`,
      body
    );
  }
  registerProgressBatch(
    body: SchedulingApiRoutesDto['registerProgress']['body']
  ): Observable<{ toRemove: boolean }> {
    return this.http.post<{ toRemove: boolean }>(
      `${this.urlRoute}/batch/register-progress`,
      body
    );
  }

  registerProgressWorkorderOperators(
    body: CreateReportOrderDto
  ): Observable<ReportWorkorderInterface> {
    return this.http.post<ReportWorkorderInterface>(
      `${this.urlRoute}/work-order/register-progress-operators`,
      body
    );
  }

  registerProgressSubWorkOrder(
    body: SchedulingApiRoutesDto['registerProgress']['body']
  ): Observable<any> {
    return this.http.post(
      `${this.urlRoute}/sub-work-order/register-progress`,
      body
    );
  }

  registerProgressStage(
    body: SchedulingApiRoutesDto['registerStageProgress']['body']
  ): Observable<any> {
    return this.http.post(`${this.urlRoute}/sub-work-order/finish-stage`, body);
  }

  getOperations(itemId: number): Observable<ItemOperations[]> {
    return this.http.get<ItemOperations[]>(
      `${this.urlRoute}/work-order/operations?itemId=${itemId}`
    );
  }

  getWorkOrderForLaboratoryTest(
    typeId: number
  ): Observable<SchedulerWorkOrder[]> {
    return this.http.get<SchedulerWorkOrder[]>(
      `${this.urlRoute}/work-order/laboratory?typeId=${typeId}`
    );
  }

  getDataOfMachineWorkOrder(machineId: number): Observable<Machine> {
    return this.http.get<Machine>(
      `${this.urlRoute}/work-order/data-by-machine/${machineId}`
    );
  }

  getOperators(typeId: number): Observable<{ operators: User[] }> {
    return this.http.get<{ operators: User[] }>(
      `${this.urlRoute}/operator/${typeId}`
    );
  }

  /**
   * @deprecated
   */
  getMachineDataByWorkOrder(
    query: SchedulingApiRoutesDto['machineDataByWorkOrder']['query']
  ): Observable<Machine[]> {
    return this.getMachineData({ ...query, items: ['workOrder'] });
  }
  getMachineData(
    query: SchedulingApiRoutesDto['machineData']['query']
  ): Observable<Machine[]> {
    const params = new HttpParams({
      fromObject: query,
    });
    return this.http.get<Machine[]>(`${this.urlRoute}/machine/data`, {
      params,
    });
  }

  getMachineDataBySubWorkOrder(
    query: SchedulingApiRoutesDto['machineDataByWorkOrder']['query']
  ): Observable<Machine[]> {
    const params = new HttpParams({
      fromObject: query,
    });
    return this.http.get<Machine[]>(
      `${this.urlRoute}/machine/data/by-sub-work-order`,
      {
        params,
      }
    );
  }

  getMachineWorkOrdersData(
    query: SchedulingApiRoutesDto['machineDataByWorkOrder']['query']
  ): Observable<MachineWorkOrder[]> {
    const params = new HttpParams({
      fromObject: query,
    });
    return this.http.get<MachineWorkOrder[]>(
      `${this.urlRoute}/machine/work-orders`,
      {
        params,
      }
    );
  }
  getWorkOrderData(
    query: SchedulingApiRoutesDto['workOrderData']['query']
  ): Observable<WorkOrder[]> {
    const params = new HttpParams({
      fromObject: query,
    });
    return this.http.get<WorkOrder[]>(`${this.urlRoute}/work-order/data`, {
      params,
    });
  }

  getSubWorkOrderData(
    query: SchedulingApiRoutesDto['subWorkOrderData']['query']
  ): Observable<SubWorkOrder[]> {
    const params = new HttpParams({
      fromObject: query,
    });
    return this.http.get<SubWorkOrder[]>(
      `${this.urlRoute}/sub-work-order/data`,
      {
        params,
      }
    );
  }

  /**
   * @description
   * Returns the MachineWorkOrder relation records (work orders currently
   * placed on each machine) for the given department. Used by the
   * Compression Molding scheduler to keep the /machine/data/by-work-order
   * response lightweight.
   */
  getMachineWorkOrders(
    query: SchedulingApiRoutesDto['machineDataByWorkOrder']['query']
  ): Observable<SchedulerWorkOrderOnMachine[]> {
    const params = new HttpParams({
      fromObject: query,
    });
    return this.http.get<SchedulerWorkOrderOnMachine[]>(
      `${this.urlRoute}/machine/work-orders`,
      { params }
    );
  }
}
